import { useState } from 'react'
import { Typography } from '~/components/common/Typography'

// Chevron Down SVG (rotated for up/down states)
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M3.33341 10L8.00008 5.33333L12.6667 10'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// Chevron Right SVG (for application rows)
const ChevronRightIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M6 3.33335L10.6667 8.00002L6 12.6667'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export interface ApplicationSchema {
  applicationId: string
  applicationName: string
  schemas: Array<{
    id: string
    name: string
    content: string
  }>
}

export interface SchemaSectionProps {
  applications: ApplicationSchema[]
  onUploadSchema: (applicationId: string) => void
}

const DeleteIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 4L4 12M4 4L12 12'
      stroke='black'
      strokeOpacity='0.45'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

export function SchemaSection({ applications, onUploadSchema }: SchemaSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Calculate total schema count across all applications
  const totalSchemaCount = applications.reduce((sum, app) => sum + app.schemas.length, 0)

  return (
    <div className='flex w-full flex-col gap-[16px]'>
      <div className='flex w-full flex-col gap-[8px] rounded-[28px] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
        {/* Header - Always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='flex w-full items-center gap-[16px] border-0 bg-transparent py-[8px] pl-[12px] pr-0 cursor-pointer'
          aria-expanded={isExpanded}
          aria-label='Toggle schema upload section'
        >
          <div className='flex flex-1 min-w-0 flex-col gap-[4px]'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-left text-[rgba(0,0,0,0.88)]'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              Add schema for downstream systems (Optional)
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-left text-[rgba(0,0,0,0.45)]'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              This will help the agent generate more accurate test cases
            </Typography>
          </div>
          <div className='flex h-full items-center justify-center px-[16px] py-[8px]'>
            <ChevronDownIcon
              className={`transform transition-transform duration-200 ${
                isExpanded ? 'rotate-0' : 'rotate-180'
              }`}
            />
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            {/* Divider */}
            <div className='h-px w-full bg-[rgba(0,0,0,0.08)]' />

            {/* Applications List */}
            <div className='flex flex-col gap-[8px]'>
              {applications.map(app => (
                <button
                  key={app.applicationId}
                  onClick={() => onUploadSchema(app.applicationId)}
                  className='flex h-[48px] w-full items-center gap-[16px] rounded-[16px] bg-[rgba(0,0,0,0.02)] px-[12px] py-[4px] border-0 cursor-pointer !bg-transparent'
                >
                  {/* Left Section: Application Name */}
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='medium'
                    className='flex-1 text-left text-[rgba(0,0,0,0.88)]'
                    style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
                  >
                    {app.applicationName}
                  </Typography>

                  {/* Right Section: Schema count or Upload text */}
                  <div className='flex items-center gap-[8px]'>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='medium'
                      className={
                        app.schemas.length > 0
                          ? 'text-[rgba(0,0,0,0.88)]'
                          : 'text-[rgba(0,0,0,0.45)]'
                      }
                      style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
                    >
                      {app.schemas.length > 0
                        ? `${app.schemas.length} set(s) added`
                        : 'Upload schema'}
                    </Typography>
                    <ChevronRightIcon />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SchemaSection
